import { createContext, useContext, useState, useCallback } from "react";
import { getAuthClient } from "@/lib/auth";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import { MyUser } from "@/types/breeze/User";
import { Tenant } from "@/types/breeze/Tenant";

import { useAuthRouting } from "@/hooks/auth/useAuthRouting";
import { useAuthStoreHydration } from "@/hooks/auth/useAuthStoreHydration";
import { useSessionManager } from "@/hooks/auth/useSessionManager";
import { useTenantAuth } from "@/hooks/auth/useTenantAuth";

// Re-export helper to not break tests
export { signOutTargetPath } from "@/hooks/auth/useAuthRouting";

const authClient = getAuthClient();

interface AuthContextType {
  currentUser: MyUser | null;
  currentTenant: Tenant | null;
  isTenantAdmin: boolean;
  loading: boolean;
  signOut: (errorMessage?: string) => Promise<void>;
  safeNavigate: (path: string) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const AuthProviderV2 = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState(true);

  const { pathname, navigate, isPublicAuthPage, safeNavigate } = useAuthRouting();
  const { storeHydrated } = useAuthStoreHydration(authClient);

  const {
    currentUser,
    currentTenant,
    isTenantAdmin,
    handleTenant,
    signOut,
  } = useTenantAuth({ pathname, navigate, authClient, setLoading });

  useSessionManager({
    authClient,
    storeHydrated,
    isPublicAuthPage,
    pathname,
    setLoading,
    onValidSession: handleTenant,
    onInvalidSession: signOut,
  });

  const boundSafeNavigate = useCallback(
    (path: string) => safeNavigate(path, currentTenant),
    [safeNavigate, currentTenant]
  );

  if (!storeHydrated) {
    return <LoadingScreen />;
  }

  if (loading && !isPublicAuthPage) {
    return <LoadingScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        currentTenant,
        loading,
        isTenantAdmin,
        signOut,
        safeNavigate: boundSafeNavigate,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProviderV2;
